// Grid for the main Playbook page: content left, HUD-track right.
// GoodEndingSiteHeader reuses it to match byline width to the column.
// The columns/gap read from --plana-* vars so the grid track and the
// fixed HUD's placement derive from the same numbers. The variable gap
// is desktop-only (md:) — on mobile the layout stacks single-column, so
// keep a normal row gap there rather than the wide center gutter.
//
// Track sizing matters here: the LEFT (text) track is minmax(0,700px)
// so it holds a true 700px whenever there's room and only shrinks once
// the viewport is genuinely too narrow. The RIGHT track is a plain `1fr`
// — its occupant (the HUD wrapper) is `hidden md:block` with a
// position:fixed child, so it contributes ZERO intrinsic width and the
// track is pure spacer. Making it `1fr` (not minmax(0,455px)) lets it
// absorb ALL sub-pixel rounding slack between the band's width and the
// grid's tracks, so that slack never gets stolen from the visible text
// column (which otherwise flexed ~12px under its 700px target).
export const MAIN_CONTENT_GRID_CLASSES =
  "grid gap-6 md:gap-[var(--plana-center-gutter)] md:grid-cols-[minmax(0,var(--plana-text-col))_1fr]"

// Shared horizontal geometry. The layout is one cluster —
//   [ side gutter ][ text col ][ center gutter ][ HUD col ][ side gutter ]
// — centered on the page. Everything (band width, HUD `right`, nav
// `right`) is derived from these numbers, so the two side gutters are
// visually equal at every width and the center gutter never drops below
// its minimum.
//
// "Side gutter" here means the space the *reader sees*: viewport edge ->
// text on the left, and HUD -> viewport edge on the right. The text sits
// inside the band's horizontal padding, so that padding is folded into
// the geometry to keep both perceived gutters identical.

// Fixed left text-column width.
export const TEXT_COL_PX = 700

// Fixed right HUD-column width (matches the HUD's own w-[455px]).
export const HUD_COL_PX = 455

// Band's desktop horizontal padding (px-6 = 1.5rem), kept in sync with
// the `sm:px-6` className on the band. The text starts this far in from
// the band's left edge, so it counts toward the visible left gutter.
export const BAND_EDGE_PADDING_PX = 24

// Minimum outer side gutter so the cluster never touches the viewport.
// Kept very small (a quarter of the comfortable ~24px) so that as the
// viewport narrows the side gutters collapse this far BEFORE the text
// column has to give up its full width — i.e. the reading column keeps
// 700px over a wider range of screens, at the cost of tighter edges.
// (Declared before the center gutter because that formula reads it.)
export const SIDE_GUTTER_MIN_PX = 6

// Minimum space between the text column and the HUD. The gutter
// collapses to this floor as the page narrows, ceding its space to the
// text column so 700px is preserved as long as possible.
export const CENTER_GUTTER_MIN_PX = 50

// Cap so text and HUD don't drift absurdly far apart on wide screens.
// Scaled up 1.75x from the previous 160 so the gutter STARTS wider.
export const CENTER_GUTTER_MAX_PX = 280

// Fraction of the viewport the center gutter grows by (the `Nvw` term
// in the clamp). 1.75x the previous 0.06 so the wide-screen gutter
// starts correspondingly wider. Exported so the galaxy-centering JS in
// Playbook.tsx reads the SAME number instead of re-hardcoding it.
export const CENTER_GUTTER_VW_FRACTION = 0.105

// Space still available for the center gutter once the text column
// (700px), the HUD, both band paddings, and the two floored side gutters
// have taken their share. When the viewport is wide this is large and
// doesn't bind; as it narrows this shrinks toward — then past — the
// gutter's floor. Feeding it into the clamp's middle term via min()
// makes the CENTER GUTTER give up its width to the text column first, so
// the 700px column is preserved as long as geometrically possible.
export const CENTER_GUTTER_AVAILABLE_CSS =
  `calc(100vw - ${2 * BAND_EDGE_PADDING_PX + TEXT_COL_PX + HUD_COL_PX + 2 * SIDE_GUTTER_MIN_PX}px)`

// The center gutter: grows with the viewport (VW_FRACTION term) up to
// the wide-screen cap, but never claims more than is left after the text
// column is satisfied (the AVAILABLE term), and never drops below the
// floor. So on wide screens it starts wide, then collapses toward the
// floor as the page narrows — protecting the 700px text column — before
// the text itself is finally forced to squeeze below the floor width.
export const CENTER_GUTTER_CSS =
  `clamp(${CENTER_GUTTER_MIN_PX}px,` +
  ` min(${CENTER_GUTTER_VW_FRACTION * 100}vw, ${CENTER_GUTTER_AVAILABLE_CSS}),` +
  ` ${CENTER_GUTTER_MAX_PX}px)`

// The band box holds, left to right: left padding + text col + center
// gutter + HUD col + right padding. The text sits behind the left
// padding and the fixed HUD aligns to the band's right *content* edge
// (band edge minus right padding), so both paddings are real and both
// count. The band is centered by giving it this width and equal margins.
export const BAND_WIDTH_CSS =
  `calc(${2 * BAND_EDGE_PADDING_PX}px + ${TEXT_COL_PX}px` +
  ` + ${CENTER_GUTTER_CSS} + ${HUD_COL_PX}px)`

// One canonical side gutter: half of whatever's left after the band,
// floored at the minimum. Both left and right use THIS single value, so
// the outer gutters are always identical — no separate ± shift that can
// diverge (the old bug). On narrow screens it pins to the minimum and
// the center gutter / columns absorb the squeeze.
export const SIDE_GUTTER_CSS =
  `max(${SIDE_GUTTER_MIN_PX}px, calc((100vw - ${BAND_WIDTH_CSS}) / 2))`

// Band margins: the same side gutter on both sides. Equal by
// construction — this is what fixes the uneven-gutter bug — and, since
// there is no competing maxWidth, the sole driver of the band's width
// (100vw - 2*SIDE_GUTTER), which resolves to BAND_WIDTH_CSS on wide
// screens and shrinks with the pinned min gutters on narrow ones.
export const BAND_MARGIN_LEFT_CSS = SIDE_GUTTER_CSS
export const BAND_MARGIN_RIGHT_CSS = SIDE_GUTTER_CSS

// HUD `right`: the reader's right gutter (HUD -> viewport edge) must
// equal the left gutter (viewport edge -> text). The text sits at
// SIDE_GUTTER + band padding, so the HUD's right edge sits there too —
// i.e. flush with the band's right *content* edge.
export const HUD_RIGHT_EDGE_CSS =
  `calc(${SIDE_GUTTER_CSS} + ${BAND_EDGE_PADDING_PX}px)`

// Nav `right`: the nav lives inside the band, so its right edge already
// sits at the band's right content edge — the same place the HUD's
// right edge lands. No extra offset needed.
export const NAV_RIGHT_FROM_BAND_CSS = `0px`

// Effective text-column width. On wide/medium screens this is the full
// TEXT_COL_PX (700). But below the geometric minimum — where even a
// fully-collapsed center gutter and floored side gutters can't fit the
// whole cluster — the text column MUST shrink, or it would run under the
// fixed HUD (which sits at HUD_RIGHT_EDGE regardless of the grid). The
// second term is exactly the room left for text once its left gutter,
// the MINIMUM center gutter, and the HUD's reserved right block are
// subtracted from the viewport:
//   100vw
//     - 2*SIDE_GUTTER            (equal left/right gutters)
//     - 2*BAND_EDGE_PADDING      (text's left inset + HUD's right inset)
//     - HUD_COL                  (the fixed HUD's width)
//     - CENTER_GUTTER_MIN        (never let text get within 50px of HUD)
// so the text can never overlap the >=50px center gutter or the HUD.
export const TEXT_COL_WIDTH_CSS =
  `min(${TEXT_COL_PX}px, calc(100vw - 2 * ${SIDE_GUTTER_CSS}` +
  ` - ${2 * BAND_EDGE_PADDING_PX + HUD_COL_PX + CENTER_GUTTER_MIN_PX}px))`
